import { Helmet } from "react-helmet-async";
import { SITE } from "../data/siteData";

/**
 * SchemaOrg — injects sitewide JSON-LD structured data.
 *
 * Includes:
 *  - Organization schema (all pages via Layout)
 *  - ProfessionalService / LocalBusiness schema (all pages)
 *
 * Usage: render once in your root Layout or App.js
 */
const SchemaOrg = () => {
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE.url}/#organization`,
    name: SITE.name,
    url: SITE.url,
    logo: {
      "@type": "ImageObject",
      url: `${SITE.url}/logo.png`,
      width: 240,
      height: 56,
    },
    description: SITE.description,
    email: SITE.email,
    telephone: SITE.phone,
    sameAs: [
      SITE.social.linkedin,
      SITE.social.twitter,
      SITE.social.youtube,
    ],
    contactPoint: {
      "@type": "ContactPoint",
      telephone: SITE.phone,
      contactType: "customer service",
      email: SITE.email,
      availableLanguage: ["English", "Hindi"],
      areaServed: "IN",
    },
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": ["ProfessionalService", "LocalBusiness"],
    "@id": `${SITE.url}/#localbusiness`,
    name: SITE.name,
    url: SITE.url,
    description: SITE.description,
    telephone: SITE.phone,
    email: SITE.email,
    priceRange: "₹₹₹",
    currenciesAccepted: "INR",
    paymentAccepted: "Bank Transfer, Cheque",
    areaServed: {
      "@type": "Country",
      name: "India",
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Trade Compliance & Customs Advisory Services",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Customs & Trade Compliance",
            url: `${SITE.url}/services/customs-trade-compliance`,
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "FTA Advisory & Utilisation",
            url: `${SITE.url}/services/free-trade-agreements`,
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Duty Optimisation",
            url: `${SITE.url}/services/duty-optimisation`,
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "MOOWR Advisory",
            url: `${SITE.url}/services/moowr`,
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "AEO Certification",
            url: `${SITE.url}/services/aeo`,
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "DGFT Licensing",
            url: `${SITE.url}/services/dgft-licensing`,
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "BIS Compliance",
            url: `${SITE.url}/services/bis-compliance`,
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "EPR Compliance",
            url: `${SITE.url}/services/epr-compliance`,
          },
        },
      ],
    },
    sameAs: [
      SITE.social.linkedin,
      SITE.social.twitter,
      SITE.social.youtube,
    ],
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE.url}/#website`,
    url: SITE.url,
    name: SITE.name,
    description: SITE.description,
    publisher: {
      "@id": `${SITE.url}/#organization`,
    },
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE.url}/services?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
    </Helmet>
  );
};

export default SchemaOrg;
